import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollProgress from '../components/ScrollProgress';
import { useLanguage } from '../contexts/LanguageContext';
import { mockData } from '../data/mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  MessageCircle,
} from 'lucide-react';

const ContactPage = () => {
  const { t, language } = useLanguage();

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.address'),
      content: `${mockData.company.address.street}, ${mockData.company.address.zip} ${mockData.company.address.city}`,
      link: 'https://maps.google.com/?q=Ellerstraße+53,+40227+Düsseldorf',
    },
    {
      icon: Phone,
      title: t('contact.phone'),
      content: '0159 0226 8861',
      link: `tel:${mockData.company.phone}`,
    },
    {
      icon: Mail,
      title: t('contact.email'),
      content: mockData.company.emailInfo,
      link: `mailto:${mockData.company.emailInfo}`,
    },
    {
      icon: Clock,
      title: language === 'de' ? 'Antwortzeit' : 'Response Time',
      content: language === 'de' 
        ? 'Alle Anfragen werden innerhalb von 24 Stunden beantwortet' 
        : 'All inquiries are answered within 24 hours',
      link: null,
    },
  ];

  return (
    <>
      <ScrollProgress />
      <Header />
      <main className="pt-24">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="container-custom">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
                {t('contact.title')}
              </h1>
              <p className="text-xl text-neutral-600">
                {t('contact.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-8">
                  {language === 'de' ? 'Kontaktinformationen' : 'Contact Information'}
                </h2>

                <div className="space-y-6 mb-10">
                  {contactInfo.map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="p-3 bg-red-50 rounded-xl flex-shrink-0">
                        <item.icon className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-neutral-900 mb-1">
                          {item.title}
                        </h3>
                        {item.link ? (
                          <a
                            href={item.link}
                            className="text-neutral-600 hover:text-red-600 transition-colors"
                            target={item.link.startsWith('http') ? '_blank' : undefined}
                            rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          >
                            {item.content}
                          </a>
                        ) : (
                          <p className="text-neutral-600">{item.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Second Email */}
                <div className="flex items-start gap-4 mb-10">
                  <div className="p-3 bg-red-50 rounded-xl flex-shrink-0">
                    <Mail className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-1">
                      {language === 'de' ? 'Weitere E-Mail' : 'Alternative Email'}
                    </h3>
                    <a
                      href={`mailto:${mockData.company.email}`}
                      className="text-neutral-600 hover:text-red-600 transition-colors"
                    >
                      {mockData.company.email}
                    </a>
                  </div>
                </div>

                {/* WhatsApp CTA */}
                <a
                  href={mockData.company.social.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-xl font-semibold transition-colors"
                >
                  <MessageCircle className="w-6 h-6" />
                  <span>
                    {language === 'de'
                      ? 'Schreiben Sie uns auf WhatsApp'
                      : 'Message us on WhatsApp'}
                  </span>
                </a>

                {/* Map */}
                <div className="mt-10 rounded-2xl overflow-hidden shadow-lg h-[300px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2498.8651789897597!2d6.783387776893945!3d51.21491652712879!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8ca1a9b0d5c5d%3A0x4d95f6b75a9a6c5!2sEllerstra%C3%9Fe%2053%2C%2040227%20D%C3%BCsseldorf!5e0!3m2!1sde!2sde!4v1699999999999!5m2!1sde!2sde"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Standort"
                  />
                </div>
              </div>

              {/* Contact Form - Direct FormSubmit */}
              <div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-8">
                  {language === 'de' ? 'Nachricht senden' : 'Send a Message'}
                </h2>

                <form
                  action="https://api.web3forms.com/submit"
                  method="POST"
                  className="bg-neutral-50 rounded-2xl p-8"
                >
                  {/* Web3Forms Configuration */}
                  <input type="hidden" name="access_key" value="d8528687-1ecb-4add-b8b0-ca987b425652" />
                  <input type="hidden" name="subject" value="Neue Kontaktanfrage - Erste-Hilfe Fokus" />
                  <input type="hidden" name="redirect" value="https://erstehilfe-fokus.de/#/kontakt?success=true" />
                  
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="name">{t('contact.form.name')} *</Label>
                      <Input
                        id="name"
                        name="name"
                        required
                        className="mt-2 bg-white"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">{t('contact.form.email')} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="mt-2 bg-white"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject">{t('contact.form.subject')}</Label>
                      <Input
                        id="subject"
                        name="subject"
                        className="mt-2 bg-white"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">{t('contact.form.message')} *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={5}
                        required
                        className="mt-2 bg-white"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 group"
                    >
                      <span>{t('contact.form.send')}</span>
                      <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </form>

                {/* Info text */}
                <p className="mt-4 text-sm text-neutral-500 text-center">
                  {language === 'de' 
                    ? 'Ihre Nachricht wird an info@erstehilfe-fokus.de gesendet.'
                    : 'Your message will be sent to info@erstehilfe-fokus.de.'}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ContactPage;
